import { Line, type LineConfig } from '@ant-design/charts';
import { format } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo } from 'react';
import { dateOf } from './chartDates';

/**
 * ONE line chart for numbers over time — shared (D-entries-over-time-2026-10-07) by the client hub's worksheet
 * "Over time", and — when they are built — E37's diary-card graph and E55's ProcessTrackingSeries chart. It draws
 * what it is given and nothing else: no trend line, no threshold band, no verdict (the board's rule — a
 * description, never a judgement). One colour per series; the x axis is time, the y axis is the data's own
 * scale (or `yRange`, e.g. a 0–10 scale).
 *
 * The canvas is not readable by a screen reader or a test, so the chart's numbers are mirrored in the DOM:
 * `data-series` on the wrapper lists `key:count:latest` for every series, and `role="img"` + `aria-label` say
 * what it shows. Callers render their own legend / table beside it (the numbers as text).
 */
export type SeriesChartPoint = { x: string; y: number };

export type SeriesChartSeries = {
  key: string;
  label: string;
  points: SeriesChartPoint[];
};

export const SERIES_COLORS = [
  '#8b4a2f',
  '#2e6e86',
  '#7a5aa6',
  '#4f7a4f',
  '#b0833a',
  '#a34a6a',
] as const;

export const seriesColor = (index: number): string =>
  SERIES_COLORS[index % SERIES_COLORS.length];

type Props = {
  series: SeriesChartSeries[];
  /** The y axis' fixed range; omitted = the data's own. */
  yRange?: [number, number];
  height?: number;
  locale?: 'en' | 'ru';
  ariaLabel: string;
  testId?: string;
};


export const SeriesChart = ({
  series,
  yRange,
  height = 280,
  locale = 'en',
  ariaLabel,
  testId = 'series-chart',
}: Props) => {
  const config = useMemo(() => {
    const dateLocale = locale === 'ru' ? ru : enUS;
    const data = series.flatMap((s) =>
      s.points.map((p) => ({ x: p.x, y: p.y, series: s.label })),
    );
    return {
      data,
      xField: 'x',
      yField: 'y',
      seriesField: 'series',
      color: series.map((_, i) => seriesColor(i)),
      height,
      autoFit: true,
      legend: false,
      point: { size: 4, shape: 'circle' },
      lineStyle: { lineWidth: 2 },
      xAxis: {
        type: 'time',
        label: {
          formatter: (v: unknown) =>
            format(dateOf(v), 'd MMM', { locale: dateLocale }),
        },
      },
      yAxis: yRange
        ? { min: yRange[0], max: yRange[1] }
        : { min: 0, nice: true },
      tooltip: {
        title: (t: unknown) =>
          format(dateOf(t), 'd MMM yyyy, HH:mm', { locale: dateLocale }),
      },
      animation: false,
    } as unknown as LineConfig;
  }, [series, yRange, height, locale]);

  const summary = series
    .map(
      (s) =>
        `${s.key}:${s.points.length}:${
          s.points.length ? s.points[s.points.length - 1].y : ''
        }`,
    )
    .join(' ');

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      data-series={summary}
      style={{ width: '100%' }}
    >
      <Line {...config} />
    </div>
  );
};
